import { graphHooks } from '@tunasong/graph-lib/react'
import { matchAll } from '@tunasong/models'
import type { Entity, Persisted, UserSettings, UserSettingsData } from '@tunasong/schemas'
import { deterministicShortUuid, isEntity, isPersisted } from '@tunasong/schemas'
import { useEffect, useState } from 'react'
import { useCurrentUser } from '@tunasong/redux'

/**
 * GH #1635 / TUNASONG-W6/W7: in-flight `usersettings` creates, keyed on
 * `${parentId}:${userId}`. `usersettings` is a singleton per (parent, user),
 * but the guard used to be a per-hook-instance ref — so when two surfaces
 * mounted `useUserSettings` for the same entity before settings existed (e.g.
 * the transpose sync + another panel in the same tab), both passed the
 * read-before-create check and each called `createEntity`, producing duplicate
 * rows that then made `useEntityChild` flap/spam Sentry. A module-level lock is
 * shared across every mounted instance, so concurrent creates collapse to one.
 *
 * This closes the same-tab multi-surface race. The cross-tab / cross-device race
 * is closed by `userSettingsId` below.
 */
const creatingSettings = new Set<string>()

/**
 * GH #1635: the id a new `usersettings` child is created under, derived from (parent, user). Two tabs or devices
 * that both find no settings create under the SAME id, and the store's conditional create (`attribute_not_exists(id)`)
 * rejects the second with a 409 instead of storing a duplicate row. Existing rows keep their random ids; they are
 * still found by the type + owner filter, so no migration is needed.
 */
const userSettingsId = (parentId: string, userId: string) =>
  deterministicShortUuid(`usersettings:${parentId}:${userId}`)

/**
 * The create lost to a create of the same id: 409 'duplicate-entity' from an EntityStore, or the 500
 * 'ConditionalCheckFailed' of a deployment that predates it (same test as `classifyReplayResult` in @tunasong/redux).
 */
const isDuplicateCreateError = (error: unknown) => {
  if (typeof error !== 'object' || error === null) {
    return false
  }
  const { status, data } = error as { status?: unknown; data?: unknown }
  const body = JSON.stringify(data ?? null)
  return (
    (status === 409 && body.includes('duplicate-entity')) || (status === 500 && body.includes('ConditionalCheckFailed'))
  )
}

/** Get the child entity matching the filter. If there are > 1 children matching the critiera this hook will throw */
export const useUserSettings = (entity?: Persisted<Entity>) => {
  const { userId } = useCurrentUser()
  /** Settings are private, so we need to filter for the userId */
  const filter = matchAll(
    e => isEntity(e) && e.type === 'usersettings',
    e => isPersisted(e) && e.userId === userId
  )
  /** Load settings once, then just update */
  const [localUserSettings, setLocalUserSettings] = useState<UserSettingsData | null>(null)

  const { child, isSuccess, isFetching } = graphHooks.useEntityChild<Persisted<UserSettings>>({
    parentId: entity?.id,
    filter,
    /**
     * Duplicate-creation is guarded by the module-level `creatingSettings` lock below
     * (GH #1635); `useEntityChild` itself takes only `{ parentId, filter }`.
     */
  })

  const { createEntity } = graphHooks.useEntityCreate()

  const shouldCreateSettings = isSuccess && !isFetching && !child && entity

  /** Clear settings when entity changes */
  useEffect(() => {
    setLocalUserSettings(null)
  }, [entity?.id])

  /** Update from child settings if localSettings is undefined (i.e., not initialized) */
  useEffect(() => {
    if (!isSuccess || localUserSettings !== null) {
      return
    }
    // Prioritize local settings over child settings
    // We need to avoid triggering a save here
    setLocalUserSettings(localSettings => ({ ...localSettings, ...child?.settings }))
  }, [child?.settings, isSuccess, localUserSettings])

  const updateEntity = graphHooks.useEntityUpdate<UserSettings>({ debounceDelay: 100 })

  const createSettings = async (parent: Persisted<Entity>, settings: UserSettings['settings']) => {
    const createKey = `${parent.id}:${userId}`
    /** No signed-in user means no owner to derive the id from (and the private-settings filter matches nothing) */
    if (!userId || creatingSettings.has(createKey)) {
      return
    }

    creatingSettings.add(createKey)
    const id = userSettingsId(parent.id, userId)

    try {
      await createEntity({
        parent,
        entity: { id, type: 'usersettings', settings } as UserSettings,
        isPrivate: true,
      })
    } catch (error) {
      if (!isDuplicateCreateError(error)) {
        throw error
      }
      /** Another tab or device created the row first. It is this user's row (the id is derived from the user): write to it. */
      updateEntity(id, { settings })
    } finally {
      creatingSettings.delete(createKey)
    }
  }

  const setUserSettings = async (data: Partial<UserSettingsData>) => {
    const newSettings = { ...localUserSettings, ...data }

    setLocalUserSettings(newSettings)

    if (shouldCreateSettings) {
      await createSettings(entity, newSettings)
      return
    }

    /**
     * GH #1454 / TUNASONG-VW: reaching here means the `usersettings` child
     * hasn't resolved yet — the query is still loading (`!isSuccess`) or
     * refetching (`isFetching`), or there's no entity to attach to. The new
     * value is already captured in `localUserSettings` above, so skip the
     * remote write rather than asserting a child that doesn't exist yet.
     * Persisting before the query settles would either crash (the old
     * `invariant`, hit by tapping Transpose during initial load) or risk a
     * duplicate settings entity. A later settled `setUserSettings` flushes
     * the value through the create/update branches.
     */
    if (!child?.id) {
      return
    }

    updateEntity(child.id, {
      settings: newSettings,
    })
  }

  return { userSettings: localUserSettings, setUserSettings, isLoaded: isSuccess }
}
